import {useMemo} from "react";
import {useFilterPrefix} from "../../../DataTable/Filters/FilterPrefixProvider";
import {QueryFilter} from "../../../DataTable/Filters/types";
import {useQuerySearchParams} from "../../../../config/queryClient";
import {
    buildFirstBestSeriesData,
    mergeSectionScoresByTitle,
    SectionFirstBestDatum,
    SectionScoreDatum,
    SectionScoreSeriesDatum
} from "./sectionPerformanceUtils";

interface Params {
    type: 'conversation' | 'dialogue-choices';
    activityId?: string | number;
    attemptId?: string;
    defaultFilters?: { [key: string]: (string | number | string[] | number []) };
    enabled?: boolean;
}

interface Result {
    firstData: SectionScoreDatum[];
    bestData: SectionScoreDatum[];
    seriesData: SectionScoreSeriesDatum[];
    mergedRows: SectionFirstBestDatum[];
    isLoading: boolean;
}

export const useSectionPerformanceFirstBestData = ({
    type,
    activityId,
    attemptId,
    defaultFilters,
    enabled = true,
}: Params): Result => {
    const filterPrefix = useFilterPrefix();

    const query = useMemo(() => {
        const baseQuery = `/reports/${type}/section-performance/`;
        if (!attemptId) return baseQuery;
        return `${baseQuery}${attemptId}/`;
    }, [attemptId, type]);

    const mergedFilters = useMemo(() => ({
        activity: activityId || '',
        ...defaultFilters,
    }), [activityId, defaultFilters]);

    const {data: firstData = [], isLoading: isFirstLoading} = useQuerySearchParams<SectionScoreDatum[]>(
        query,
        [filterPrefix, QueryFilter.COHORTS, QueryFilter.COURSES],
        {
            defaultFilters: {...mergedFilters, first: 'true'},
            enabled,
        },
    );

    const {data: bestData = [], isLoading: isBestLoading} = useQuerySearchParams<SectionScoreDatum[]>(
        query,
        [filterPrefix, QueryFilter.COHORTS, QueryFilter.COURSES],
        {
            defaultFilters: {...mergedFilters, best: 'true'},
            enabled,
        },
    );

    const seriesData = useMemo(
        () => buildFirstBestSeriesData(firstData, bestData),
        [firstData, bestData],
    );

    const mergedRows = useMemo(
        () => mergeSectionScoresByTitle(firstData, bestData),
        [firstData, bestData],
    );

    return {
        firstData,
        bestData,
        seriesData,
        mergedRows,
        isLoading: isFirstLoading || isBestLoading,
    };
};
